import { Component } from '@theme/component';

/**
 * Promotion bar with a copyable discount code and a countdown to a wall-clock end
 * time in an IANA time zone (daylight saving time is resolved per end date).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [countdown]
 * @property {HTMLElement} [days]
 * @property {HTMLElement} [hours]
 * @property {HTMLElement} [minutes]
 * @property {HTMLElement} [seconds]
 * @property {HTMLElement} [status]
 *
 * @extends {Component<Refs>}
 */
class PromoCountdownBar extends Component {
  /** @type {number|undefined} */
  #interval = undefined;

  /** @type {number|undefined} */
  #copiedTimeout = undefined;

  /** @type {number} */
  #target = NaN;

  connectedCallback() {
    super.connectedCallback();
    this.#start();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    clearTimeout(this.#copiedTimeout);
  }

  /**
   * Copies the discount code and confirms it visually and for screen readers.
   * @param {MouseEvent} event
   */
  async copyCode(event) {
    const button = event.target instanceof HTMLButtonElement ? event.target : null;
    const code = button?.dataset.code;
    if (!button || !code) return;

    try {
      await navigator.clipboard.writeText(code);
    } catch {
      return;
    }

    button.dataset.copied = '';
    if (this.refs.status) this.refs.status.textContent = this.refs.status.dataset.copiedText ?? '';

    clearTimeout(this.#copiedTimeout);
    this.#copiedTimeout = window.setTimeout(() => {
      delete button.dataset.copied;
      if (this.refs.status) this.refs.status.textContent = '';
    }, 2000);
  }

  #start() {
    this.#stop();

    const { endDate, endTime, timeZone } = this.dataset;
    if (!endDate || !this.refs.countdown) return;

    this.#target = zonedTimeToEpoch(endDate, endTime ?? '23:59', timeZone ?? 'Europe/Vienna');
    if (!this.#tick()) return;

    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  #stop() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Renders the remaining time.
   * @returns {boolean} Whether the countdown is still running.
   */
  #tick() {
    const remaining = this.#target - Date.now();

    if (!Number.isFinite(remaining) || remaining <= 0) {
      this.#stop();
      if (this.dataset.expiredBehavior === 'hide_section') {
        this.hidden = true;
        this.previousElementSibling?.setAttribute('hidden', '');
      } else if (this.refs.countdown) {
        this.refs.countdown.hidden = true;
      }
      return false;
    }

    const total = Math.floor(remaining / 1000);
    const { days, hours, minutes, seconds } = this.refs;
    if (days) days.textContent = pad(Math.floor(total / 86400));
    if (hours) hours.textContent = pad(Math.floor((total % 86400) / 3600));
    if (minutes) minutes.textContent = pad(Math.floor((total % 3600) / 60));
    if (seconds) seconds.textContent = pad(total % 60);
    return true;
  }
}

/**
 * @param {number} value
 */
function pad(value) {
  return String(value).padStart(2, '0');
}

/**
 * Converts a wall-clock date and time in an IANA time zone to epoch milliseconds.
 * @param {string} date - YYYY-MM-DD
 * @param {string} time - HH:MM
 * @param {string} timeZone - e.g. Europe/Vienna
 * @returns {number} Epoch milliseconds, or NaN for invalid input.
 */
function zonedTimeToEpoch(date, time, timeZone) {
  const [year, month, day] = date.trim().split('-').map(Number);
  const [hour = 0, minute = 0] = time.trim().split(':').map(Number);
  if ([year, month, day, hour, minute].some((part) => !Number.isFinite(part))) return NaN;

  const wallClock = Date.UTC(year, month - 1, day, hour, minute);

  /** @type {Intl.DateTimeFormat} */
  let format;
  try {
    format = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
  } catch {
    return NaN;
  }

  /** @param {number} epoch */
  const offsetAt = (epoch) => {
    /** @type {Record<string, number>} */
    const parts = {};
    for (const { type, value } of format.formatToParts(new Date(epoch))) parts[type] = Number(value);
    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    return asUtc - Math.floor(epoch / 1000) * 1000;
  };

  // Two passes settle the offset when the first guess lands across a DST change.
  let epoch = wallClock - offsetAt(wallClock);
  epoch = wallClock - offsetAt(epoch);
  return epoch;
}

if (!customElements.get('promo-countdown-bar')) {
  customElements.define('promo-countdown-bar', PromoCountdownBar);
}
